import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

const fieldClass =
  'w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-airmax-blue';
const labelClass = 'block text-sm font-semibold text-gray-800 mb-1';

const ServiceAcknowledgment: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [serviceAddress, setServiceAddress] = useState('');
  const [ackToken, setAckToken] = useState('');
  const [ackTerms, setAckTerms] = useState(false);
  const [ackSms, setAckSms] = useState(false);
  const [signatureDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [done, setDone] = useState(false);
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('ack')?.trim() || '';
    if (!token) return;
    setAckToken(token);
    const loadPrefill = async () => {
      try {
        const response = await fetch('/api/service-acknowledgment-context.php?token=' + encodeURIComponent(token), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const data = (await response.json()) as { ok?: boolean; error?: string; fullName?: string; phone?: string; email?: string; serviceAddress?: string };
        if (!response.ok || !data.ok) throw new Error(data.error || 'Could not load your account information.');
        setFullName(data.fullName || '');
        setPhone(data.phone || '');
        setEmail(data.email || '');
        setServiceAddress(data.serviceAddress || '');
      } catch (err) {
        setStatus({ type: 'error', message: err instanceof Error ? err.message : 'Could not load your account information.' });
      }
    };
    void loadPrefill();
  }, []);

  useEffect(() => {
    const id = 'great-vibes-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-airmax-chat-ack';
    style.textContent = `
      [id*="airmax-chat"], [class*="airmax-chat"],
      [id*="airmax-widget"], [class*="airmax-widget"] {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    const w = window as Window & {
      AirmaxChat?: {
        isOpen?: boolean;
        greetingBubbleShown?: boolean;
        hideGreetingBubble?: () => void;
        hide?: () => void;
      };
    };
    if (w.AirmaxChat) {
      if (w.AirmaxChat.isOpen) {
        w.AirmaxChat.isOpen = false;
        const win = document.getElementById('airmax-chat-window');
        if (win) win.style.display = 'none';
      }
      w.AirmaxChat.greetingBubbleShown = false;
      w.AirmaxChat.hideGreetingBubble?.();
      w.AirmaxChat.hide?.();
    }

    return () => {
      document.getElementById('hide-airmax-chat-ack')?.remove();
    };
  }, []);

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const draw = async () => {
      const name = fullName.trim();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const cssW = canvas.clientWidth || 560;
      const cssH = canvas.clientHeight || 120;
      canvas.width = Math.floor(cssW * ratio);
      canvas.height = Math.floor(cssH * ratio);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      ctx.strokeStyle = 'rgba(11,107,203,0.18)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(12, cssH * 0.72);
      ctx.lineTo(cssW - 12, cssH * 0.72);
      ctx.stroke();

      if (!name) {
        setSignatureDataUrl('');
        return;
      }

      try {
        await (document as Document & { fonts?: FontFaceSet }).fonts?.load('48px "Great Vibes"');
      } catch {
        // fallback fonts still work
      }

      ctx.fillStyle = '#142033';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      let size = Math.min(64, Math.floor(cssW / Math.max(name.length * 0.42, 6)));
      size = Math.max(28, size);
      const fontStack = '"Great Vibes", "Segoe Script", "Brush Script MT", cursive';
      ctx.font = `${size}px ${fontStack}`;
      while (ctx.measureText(name).width > cssW - 36 && size > 22) {
        size -= 2;
        ctx.font = `${size}px ${fontStack}`;
      }
      ctx.fillText(name, cssW / 2, cssH * 0.52);
      setSignatureDataUrl(canvas.toDataURL('image/png'));
    };

    void draw();
  }, [fullName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);

    if (!fullName.trim() || !signatureDataUrl) {
      setStatus({ type: 'error', message: 'Type your full legal name so we can create your electronic signature.' });
      return;
    }
    if (!phone.trim()) {
      setStatus({ type: 'error', message: 'Enter the mobile phone number for SMS communications.' });
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setStatus({ type: 'error', message: 'Enter a valid email so we can send your welcome message.' });
      return;
    }
    if (!ackTerms) {
      setStatus({
        type: 'error',
        message: 'Please confirm you have read and understand the Airmax Terms and Conditions.',
      });
      return;
    }
    if (!ackSms) {
      setStatus({ type: 'error', message: 'Please confirm SMS Communications Consent.' });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('fullName', fullName.trim());
      formData.append('phone', phone.trim());
      formData.append('email', email.trim());
      formData.append('serviceAddress', serviceAddress.trim());
      formData.append('signatureDate', signatureDate);
      formData.append('signaturePng', signatureDataUrl);
      formData.append('ackTerms', '1');
      formData.append('ackSms', '1');
      if (ackToken) formData.append('ackToken', ackToken);

      const response = await fetch('/api/service-acknowledgment.php', {
        method: 'POST',
        body: formData,
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Unexpected server response. Please try again or call Airmax.');
      }
      const data = (await response.json()) as { ok?: boolean; error?: string };
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Could not save your acknowledgment.');
      }
      setDone(true);
      setStatus({
        type: 'success',
        message: 'Thank you. Your acknowledgment is recorded and a welcome email is on its way to your inbox.',
      });
    } catch (err) {
      setStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="relative py-16 bg-gradient-to-br from-airmax-blue to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Service acknowledgment
            </h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Confirm you have read Airmax Terms and Conditions and understand what you receive with our internet service.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-blue-100">
            <div className="flex items-start gap-3 mb-4">
              <DocumentTextIcon className="h-7 w-7 text-airmax-blue flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Terms and Conditions</h2>
                <p className="text-gray-600 mt-1">
                  Please review the full{' '}
                  <Link to="/terms" className="text-airmax-blue font-semibold underline underline-offset-2">
                    Airmax Terms and Conditions
                  </Link>{' '}
                  before signing. By continuing, you acknowledge that you understand the services Airmax provides,
                  equipment and payment responsibilities, and the other terms that apply to your internet service.
                </p>
              </div>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-700 list-disc pl-6">
              <li>Wireless internet access according to your selected service package</li>
              <li>Installation and support according to Airmax policies</li>
              <li>Billing, equipment, and cancellation terms as stated in the Terms and Conditions</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-amber-200">
            <div className="flex items-start gap-3 mb-4">
              <DevicePhoneMobileIcon className="h-7 w-7 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">SMS Communications Consent</h2>
                <p className="text-gray-700 mt-2 leading-relaxed">
                  By providing your mobile phone number, you agree to receive SMS messages from Airmax Internet L.L.C.
                  related to your account and services. These messages may include:
                </p>
                <ul className="mt-3 space-y-1.5 text-gray-700 list-disc pl-5">
                  <li>Installation and appointment notifications</li>
                  <li>Billing and payment reminders</li>
                  <li>Service outage notifications</li>
                  <li>Technical support communications</li>
                  <li>Account and service updates</li>
                </ul>
                <p className="text-gray-700 mt-4 leading-relaxed">
                  Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for assistance. Message frequency
                  varies. Message and data rates may apply. See our{' '}
                  <Link to="/privacy" className="text-airmax-blue font-semibold underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>

          {done ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Acknowledgment received</h2>
              <p className="text-gray-700">
                Thank you, {fullName.trim()}. Your signed acknowledgment PDF was emailed to {email.trim()}.
                Check your inbox (and spam) for “Welcome to Airmax Internet.”
              </p>
              <Link
                to="/terms"
                className="inline-block mt-6 text-airmax-blue font-semibold hover:underline"
              >
                View Terms and Conditions again
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-5">
              <div>
                <label htmlFor="fullName" className={labelClass}>
                  Full legal name *
                </label>
                <input
                  id="fullName"
                  name="fullName"
                  className={fieldClass}
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    setStatus(null);
                  }}
                  autoComplete="name"
                  required
                  placeholder="Type your full legal name"
                />
              </div>

              <div>
                <label className={labelClass}>Electronic signature</label>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <canvas
                    ref={signatureCanvasRef}
                    className="w-full h-[120px] bg-white rounded-lg border border-gray-100"
                    aria-label="Electronic signature preview"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Preview updates as you type. Signing date: {signatureDate}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className={labelClass}>
                    Mobile phone (SMS) *
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className={fieldClass}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    required
                    placeholder="For SMS notifications"
                  />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className={fieldClass}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    placeholder="We'll send your welcome email here"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="serviceAddress" className={labelClass}>
                  Service address
                </label>
                <input
                  id="serviceAddress"
                  name="serviceAddress"
                  className={fieldClass}
                  value={serviceAddress}
                  onChange={(e) => setServiceAddress(e.target.value)}
                  autoComplete="street-address"
                  placeholder="Optional"
                />
              </div>

              <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-airmax-blue focus:ring-airmax-blue"
                  checked={ackTerms}
                  onChange={(e) => setAckTerms(e.target.checked)}
                />
                <span>
                  I have read and understand the{' '}
                  <Link to="/terms" className="text-airmax-blue font-semibold underline">
                    Airmax Terms and Conditions
                  </Link>
                  , and I understand what I receive when Airmax provides internet services. *
                </span>
              </label>

              <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-airmax-blue focus:ring-airmax-blue"
                  checked={ackSms}
                  onChange={(e) => setAckSms(e.target.checked)}
                />
                <span>
                  I agree to the <strong>SMS Communications Consent</strong> above and authorize Airmax Internet to
                  text the mobile number I provided. I understand that I can reply STOP to unsubscribe or HELP for
                  assistance, message frequency varies, and message and data rates may apply. *
                </span>
              </label>

              {status && (
                <div
                  className={`rounded-lg px-4 py-3 text-sm font-medium ${
                    status.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full md:w-auto inline-flex justify-center items-center px-8 py-3.5 rounded-lg bg-airmax-blue text-white font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Saving…' : 'Sign and acknowledge'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServiceAcknowledgment;
